import React from "react";
import "./Loader.css";

/**
 * Индикаторы загрузки.
 *
 * Раньше на месте данных выводилась строка «Загрузка отчёта…»: по ней
 * нельзя отличить идущую загрузку от зависшего интерфейса.
 *
 * Что выбирать:
 *   <Skeleton …>  — структура контента известна (плитки, строки таблицы).
 *                   Заглушка занимает то же место, поэтому при появлении
 *                   данных ничего не прыгает.
 *   <Loading />   — структура неизвестна или блок маленький.
 *   <Spinner />   — внутри кнопки или рядом с полем.
 *
 * Роль ARIA обязательна: скринридер иначе не узнает, что область
 * обновляется. aria-busy вешаем на сам индикатор, а не на родителя —
 * родитель нам здесь не принадлежит.
 */

/** Крутящееся кольцо. `size`: "sm" | "md" | "lg". */
export function Spinner({ size = "md", className = "", ...rest }) {
  const cls = ["ui-spinner", size !== "md" ? `ui-spinner-${size}` : "", className]
    .filter(Boolean)
    .join(" ");
  return <span className={cls} role="status" aria-label="Загрузка" {...rest} />;
}

/**
 * Блок «идёт загрузка» со спиннером и подписью.
 *
 * @param {string} text   подпись; описывает, что именно грузится
 * @param {boolean} inline компактный вариант в строку
 */
export function Loading({ text = "Загрузка…", inline = false, className = "", ...rest }) {
  const cls = ["ui-loading", inline ? "ui-loading-inline" : "", className]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls} role="status" aria-live="polite" {...rest}>
      <Spinner size={inline ? "sm" : "md"} aria-hidden="true" aria-label={undefined} />
      <span>{text}</span>
    </div>
  );
}

/**
 * Заглушка под контент.
 *
 * @param {"text"|"cards"|"rows"|"block"} variant форма заглушки
 * @param {number} count  сколько элементов рисовать
 * @param {string} text   что грузится — уходит только в скринридер
 */
export function Skeleton({
  variant = "text",
  count = 3,
  text = "Загрузка…",
  className = "",
  style,
  ...rest
}) {
  const n = Math.max(1, count);
  const items = Array.from({ length: n }, (_, i) => i);

  let inner;
  let wrapClass;

  if (variant === "cards") {
    wrapClass = "ui-skeleton-cards";
    inner = items.map((i) => <div key={i} className="ui-skeleton ui-skeleton-card" />);
  } else if (variant === "rows") {
    wrapClass = "ui-skeleton-rows";
    inner = items.map((i) => <div key={i} className="ui-skeleton ui-skeleton-row" />);
  } else if (variant === "block") {
    wrapClass = "";
    inner = <div className="ui-skeleton" style={{ height: "100%", minHeight: 120 }} />;
  } else {
    wrapClass = "ui-skeleton-lines";
    inner = items.map((i) => <div key={i} className="ui-skeleton ui-skeleton-text" />);
  }

  return (
    <div
      className={[wrapClass, className].filter(Boolean).join(" ")}
      style={style}
      role="status"
      aria-live="polite"
      aria-busy="true"
      {...rest}
    >
      {/* Видимого текста нет — форма заглушки уже сообщает о загрузке. */}
      <span className="sr-only">{text}</span>
      {inner}
    </div>
  );
}

export default Loading;
